/**
 * UI-6b Task 16 — Dashboard renderer (read-only).
 *
 * Consumes a `DashboardLayout` (the JSONB payload persisted on the
 * `dashboards` table) and renders each placed widget inside a 12-column
 * CSS grid. The grid cell's column/row spans are mapped from the widget
 * `size` via `SIZE_TO_CLASS`.
 *
 * Used by `/supervisor` (and any other role landing page) to render the
 * role's default dashboard. The composer (Task 17) reuses this same
 * renderer for its live preview.
 *
 * Failure modes:
 *   - Unknown `widgetType` (e.g. a widget removed in a later release that
 *     still has saved layouts): render `null` for that slot rather than
 *     crashing. Silent skip is the spec-mandated behaviour
 *     (design §4.5, §6 acceptance 14).
 *   - Empty layout: renders an empty grid; the caller is responsible for
 *     deciding whether to substitute a fallback view.
 */
import type { JSX } from 'react'
import { WIDGET_REGISTRY } from './widgets/index.js'
import type { DashboardLayout, WidgetSize } from './widgets/types.js'

const SIZE_TO_CLASS: Record<WidgetSize, string> = {
  '1x1': 'col-span-3 row-span-1',
  '2x1': 'col-span-6 row-span-1',
  '1x2': 'col-span-3 row-span-2',
  '2x2': 'col-span-6 row-span-2',
}

export interface DashboardRendererProps {
  layout: DashboardLayout
}

export default function DashboardRenderer({
  layout,
}: DashboardRendererProps): JSX.Element {
  return (
    <div
      className="grid grid-cols-12 auto-rows-min gap-4 p-4"
      data-testid="dashboard-grid"
    >
      {layout.map((w) => {
        const manifest = WIDGET_REGISTRY[w.widgetType]
        if (!manifest) return null
        const Renderer = manifest.Renderer
        return (
          <div
            key={w.id}
            className={`${SIZE_TO_CLASS[w.size]} rounded border bg-white p-3`}
            data-widget-id={w.id}
            data-widget-type={w.widgetType}
          >
            <Renderer config={w.config} />
          </div>
        )
      })}
    </div>
  )
}
